import { Box } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { pt } from "date-fns/locale";
import { FC } from "react";
import { BrowserRouter } from "react-router-dom";
import { DialogProvider } from "./components/DialogManager";
import { OnlineOrdersWatcher } from "./components/OnlineOrdersWatcher";
import { ToastProvider } from "./components/UIComponents/Toast";
import { APP_SCROLL_CONTAINER_ID } from "./constants/layout";
import {
  AuthProvider,
  EmployeesProvider,
  RefOptionsProvider,
  StoreProvider,
} from "./contexts";
import { VisibleViewportProvider } from "./contexts/VisibleViewportContext";
import { useDialogFocusRestore } from "./hooks/useDialogFocusRestore";
import { useOverlayViewportClamp } from "./hooks/useOverlayViewportClamp";
import { useShadowTextareaNames } from "./hooks/useShadowTextareaNames";
import { useVisibleViewport } from "./hooks/useVisibleViewport";
import AppRouter from "./routes/AppRouter";
import { surface, visibleViewport } from "./theme";

/*
 * Module scope, not inside the component: rebuilding this every render made
 * LocalizationProvider reconstruct its date adapter each time App re-rendered.
 * Capitalizes month names, both full and short.
 */
const customPtLocale = {
  ...pt,
  localize: {
    ...pt.localize,
    month: (
      n: Parameters<typeof pt.localize.month>[0],
      options?: Parameters<typeof pt.localize.month>[1],
    ) => {
      const month = pt.localize.month(n, options);
      return month.charAt(0).toUpperCase() + month.slice(1);
    },
  },
};

const AppInner: FC = () => {
  // Puts focus back on whatever opened a dialog; see the hook for why MUI's own
  // restore is disabled in the theme.
  useDialogFocusRestore();

  // Names MUI's hidden autosize twin textareas; see the hook for why this has to
  // happen in the DOM.
  useShadowTextareaNames();

  /*
   * How much of our viewport a host frame actually shows. Zero unless we are
   * embedded in a frame taller than the space it gives us, in which case the
   * viewport-anchored layers in the theme follow the visible band instead of
   * the frame. Standalone rendering fills its viewport; the host owns embedded
   * iframe sizing.
   */
  const visibleInsets = useVisibleViewport();
  const isEmbedded = window.parent !== window;

  useOverlayViewportClamp(visibleInsets);

  return (
    <VisibleViewportProvider value={visibleInsets}>
      <AuthProvider>
        <LocalizationProvider
          dateAdapter={AdapterDateFns}
          adapterLocale={customPtLocale}
        >
          <StoreProvider>
            <EmployeesProvider>
              <RefOptionsProvider>
                <ToastProvider>
                  {/* Keeps the online-orders badge current on every route. */}
                  <OnlineOrdersWatcher />
                  <DialogProvider>
                    {/*
                    Embedded hosts own the iframe height while route content
                    grows this shell naturally. Standalone rendering keeps its
                    viewport floor. The resize hook reports the natural height
                    to Loop Hubs; overlays independently follow the measured
                    visible band.

                    The scroll room is read from the custom property rather
                    than from this render's `visibleInsets`, because the resize
                    hook has to deduct exactly the room added here. Going
                    through React state let the two fall out of phase — a
                    measurement taken after the padding changed but before the
                    hook's effect re-ran was wrong by one whole inset, which
                    resized the frame, which changed the inset again. The
                    property is one value both sides resolve in the same style
                    recalculation, so they cannot disagree. It is `0px` unless
                    the app is the only thing on screen that can scroll and the
                    host has left part of the frame outside the visible band.
                  */}
                    <Box
                      id={APP_SCROLL_CONTAINER_ID}
                      sx={theme => ({
                        // The host owns iframe sizing when embedded. Keeping a
                        // viewport floor there can preserve a stale fullscreen
                        // height after the host returns to its normal mode.
                        minHeight: isEmbedded ? 0 : "100vh",
                        padding: surface.pageGutter,
                        paddingBottom: `calc(${surface.pageGutter} + ${visibleViewport.scrollRoom})`,
                        [theme.breakpoints.down("sm")]: {
                          padding: surface.pageGutterCompact,
                          paddingBottom: `calc(${surface.pageGutterCompact} + ${visibleViewport.scrollRoom})`,
                        },
                      })}
                    >
                      <AppRouter />
                    </Box>
                  </DialogProvider>
                </ToastProvider>
              </RefOptionsProvider>
            </EmployeesProvider>
          </StoreProvider>
        </LocalizationProvider>
      </AuthProvider>
    </VisibleViewportProvider>
  );
};

const App: FC = () => (
  <BrowserRouter>
    <AppInner />
  </BrowserRouter>
);

export default App;
